import { Box, Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import GroupsIcon from "@mui/icons-material/Groups";
import ArchiveIcon from "@mui/icons-material/Archive";
import DeleteIcon from "@mui/icons-material/Delete";
import { getCategoryLabel } from "../constants/contentOptions";

function formatDate(iso) {
  if (!iso) return "-";

  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";

  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();

  return `${dd}/${mm}/${yyyy}`;
}

function StatusChip({ status }) {
  const labels = {
    DRAFT: "Borrador",
    PUBLISHED: "Publicado",
    ARCHIVED: "Archivado",
  };

  const styles = {
    DRAFT: { bgcolor: "neutral.300", color: "primary.900" },
    PUBLISHED: { bgcolor: "#b9f6ca", color: "primary.900" },
    ARCHIVED: { bgcolor: "#ffcdd2", color: "primary.900" },
  };

  return (
    <Chip
      label={labels[status] || status}
      size="small"
      sx={{
        fontWeight: 700,
        borderRadius: "10px",
        ...styles[status],
      }}
    />
  );
}

function CategoryChip({ label, itemType }) {
  const categoryLabels = {
    POLITICA: "Política",
    MANUAL: "Manual",
    PROCEDIMIENTO: "Procedimiento",
    CAPACITACION: "Capacitación",
    COMUNICADO: "Comunicado",
    EVALUACION: "Evaluación",
  };

  return (
    <Chip
      label={categoryLabels[label] || label}
      size="small"
      sx={{
        fontWeight: 700,
        borderRadius: "10px",
        bgcolor: itemType === "EVALUATION" ? "secondary.200" : "neutral.100",
        color: "primary.900",
        "& .MuiChip-icon": {
          color: "primary.900",
          fontSize: 16,
        },
      }}
    />
  );
}

function EvaluationSummary({ summary }) {
  if (!summary) return null;

  return (
    <Typography sx={{ mt: 0.3, fontSize: 11.5, fontWeight: 700, color: "neutral.600" }}>
      Resueltas: {summary.submitted ?? 0} · Pendientes: {summary.pending ?? 0} · En progreso:{" "}
      {summary.inProgress ?? 0}
    </Typography>
  );
}

export default function ContenidoTableRow({
  row,
  onEdit,
  onScope,
  onResults,
  onArchive,
  onDelete,
}) {
  const isEvaluation = row.itemType === "EVALUATION";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        px: 2,
        py: 1.2,
        borderBottom: "1px solid rgba(0,0,0,0.05)",
      }}
    >
      <Box sx={{ flex: 2.4, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "primary.900" }}>
          {row.title}
        </Typography>

        {isEvaluation && <EvaluationSummary summary={row.resultsSummary} />}
      </Box>

      <Box sx={{ flex: 1 }}>
        <CategoryChip label={getCategoryLabel(row.category)} />
      </Box>

      <Box sx={{ flex: 1 }}>
        <StatusChip status={row.status} />
      </Box>

      <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 700, color: "neutral.700" }}>
        {formatDate(row.updatedAt)}
      </Typography>

      <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 700, color: "neutral.700" }}>
        {row.authorDisplayName || "-"}
      </Typography>

      <Stack direction="row" spacing={1} sx={{ flex: 1, alignItems: "center" }}>
        <Tooltip title="Editar">
          <IconButton
            size="small"
            onClick={() => onEdit(row)}
            sx={{ bgcolor: "neutral.100", "&:hover": { bgcolor: "neutral.200" } }}
          >
            <EditIcon sx={{ fontSize: 16, color: "primary.900" }} />
          </IconButton>
        </Tooltip>

        {isEvaluation ? (
          <Tooltip title="Resultados">
            <IconButton
              size="small"
              onClick={() => onResults(row)}
              sx={{ bgcolor: "secondary.200", "&:hover": { bgcolor: "secondary.300" } }}
            >
              <GroupsIcon sx={{ fontSize: 16, color: "primary.900" }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Alcance">
            <IconButton
              size="small"
              onClick={() => onScope(row)}
              sx={{ bgcolor: "neutral.100", "&:hover": { bgcolor: "neutral.200" } }}
            >
              <GroupsIcon sx={{ fontSize: 16, color: "primary.900" }} />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title="Archivar">
          <IconButton
            size="small"
            onClick={() => onArchive(row)}
            sx={{ bgcolor: "#ffcdd2", "&:hover": { bgcolor: "#ffb3ba" } }}
          >
            <ArchiveIcon sx={{ fontSize: 16, color: "primary.900" }} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Borrar">
          <IconButton
            size="small"
            onClick={() => onDelete(row)}
            sx={{ bgcolor: "neutral.100", "&:hover": { bgcolor: "neutral.200" } }}
          >
            <DeleteIcon sx={{ fontSize: 16, color: "#d32f2f" }} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
}